import { useCallback, useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'antd';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Pages from 'libs/api/admin/pages';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import ErrorHelper from 'helpers/ErrorHelper';
import { useApi } from 'hooks/useApi';
import { pagesAdapter } from './pages.adapter';

const api = new Pages();

const usePages = (isMainPage = true) => {
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const pages = useRecoilValue(getPage);

  const {
    data,
    singleItem: singleData,
    loading,
    onChangeTableFilter,
    onDelete: handleDeleteCategory,
    updateInProcess,
    setUpdateInProcess
  } = useApi({
    resourceKey: 'pages',
    singleResourceKey: 'page',
    adapter: pagesAdapter,
    api,
    isMainPage
  });

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        sorter: () => {},
      },
      {
        title: t('Url'),
        dataIndex: 'slug',
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: '',
        dataIndex: 'id',
        align: 'right',
        render: (data, row) =>
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false ||
            row.can_destroy !== false) && (
            <Row gutter={16} justify={'end'}>
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
              {row.can_destroy !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
  }, [t, pathname, pages?.ability?.can_read]);

  const handleCreateOrUpdate = useCallback(async (values) => {
    setUpdateInProcess(true);
    try {
      const data = {
        page: {
          ...values,
        },
      };
      if (!id) {
        await api.create({ data });
        navigate('/admin/pages');
      } else {
        await api.put(id, { data });
        navigate(`/admin/pages/${id}?success=true`);
      }
    } catch (e) {
      form.setFields(
        ErrorHelper.getErrorsForForm(e?.response?.data || {}, {
          shopping_center: 'shopping_center_id',
        }),
      );
      if (e?.response?.data?.body) ErrorHelper.setErrors(e?.response?.data?.body || []);
    } finally {
      setUpdateInProcess(false);
    }
  }, [form, id, navigate, setUpdateInProcess]);

  useEffect(() => {
    (async () => {
      if (id) {
        setUpdateInProcess(true);
        try {
          const {
            data: { page },
          } = await api.get(id);
          let fieldsValues = [];
          for (let key in page) {
            if (key !== 'id' && key !== 'shopping_center') {
              fieldsValues.push({
                name: key,
                value: page[key],
              });
            } else if (key === 'shopping_center') {
              fieldsValues.push({
                name: 'shopping_center_id',
                value: page[key].id,
              });
            }
          }
          form.setFields(fieldsValues);
        } catch (e) {
          console.log(e);
        } finally {
          setUpdateInProcess(false);
        }
      }
    })();
  }, [id, form, setUpdateInProcess]);

  return {
    columns,
    data,
    singleData,
    loading,
    onChangeTableFilter,
    updateInProcess,
    form,
    shoppingCenters,
    handleCreateOrUpdate,
    handleDeleteCategory,
  };
};

export default usePages;
